/// <amd-module name="Wit/QueriesHub/Editor/TreeQueryEditor" />

import * as React from "react";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Dropdown } from "VSSUI/Dropdown";
import { IListBoxItem } from "VSSUI/ListBox";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { LinkQueryMode } from "Wit/Common/QueryUtils/QueryTypeConstants";
import { ClauseType, IEditInfo, IFilter, IWorkItemLinkType } from "Wit/QueriesHub/Common/Interfaces";
import * as Resources from "Wit/QueriesHub/Resources";
import { IFieldProjectData } from "Wit/WorkItemDataManager/Contracts";
import { EditorTable } from "./EditorTable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";

export interface ITreeQueryEditorProps {
    crossProject: boolean;
    editInfo: IEditInfo;
    fieldProjectData: IFieldProjectData;
    fields: IFieldEntry[];
    projectId: string;
    workItemLinkTypes: IWorkItemLinkType[];
    setIsDirty: (isDirty: boolean) => void;
    setFilterState: (filter: IFilter, clauseType: ClauseType) => void;
    setTreeState: (mode?: number, linkTypes?: string, treeLinkTypes?: string, checked?: boolean) => void;
    setStateAddRow: (clauseType: ClauseType, rowIndex?: number) => void;
    setStateRemoveRow: (clauseType: ClauseType, rowIndex: number) => void;
    setClauseFieldState: (field: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseOperatorState: (operator: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseValueState: (value: string, rowIndex: number, clauseType: ClauseType) => void;
    setClauseLogicalOperatorState: (logicalOperator: string, rowIndex: number, clauseType: ClauseType) => void;
}

export class TreeQueryEditor extends VssComponent<ITreeQueryEditorProps> {
    private filterOptionsDropdownWidth: number = 300;
    private treeTypeDropdownWidth: number = 200;

    private filterOptionsItems: IListBoxItem[] = [];
    private filterOptionsSelection: DropdownSelection;

    private treeTypeItems: IListBoxItem[] = [];
    private treeTypeSelection: DropdownSelection;

    constructor(props: ITreeQueryEditorProps, context: IVssComponentContext) {
        super(props, context);
        this.initializeFilterOptionsDropdown();
        this.initializeTreeTypeDropdown();
    }

    public render(): JSX.Element {
        return (
            <>
                <div>
                    <div className="flex-row padding-vertical-16">{Resources.QueriesEditorLinkedText}</div>
                    <EditorTable clauseType={ClauseType.Tree} {...this.props} />
                </div>
                <div className={isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.EnableNewBoardsQueriesNewDesign) ? "flex-row padding-top-16 flex-wrap linked-editors" : "flex-row padding-top-16 linked-editors"}>
                    <div className="flex-column rhythm-vertical-4">
                        <span>{Resources.FilterOptions}</span>
                        <Dropdown
                            items={this.filterOptionsItems}
                            selection={this.filterOptionsSelection}
                            width={this.filterOptionsDropdownWidth}
                            onSelect={(event, filterOptionItem) => {
                                this.props.setTreeState(+filterOptionItem.id);
                                this.props.setIsDirty(true);
                            }}
                        />
                    </div>
                    <div className="flex-column padding-horizontal-16 rhythm-vertical-4">
                        <span>{Resources.TypeOfTree}</span>
                        <Dropdown
                            items={this.treeTypeItems}
                            selection={this.treeTypeSelection}
                            width={this.treeTypeDropdownWidth}
                            onSelect={(event, treeTypeItem) => {
                                this.props.setTreeState(undefined, undefined, treeTypeItem.id);
                                this.props.setIsDirty(true);
                            }}
                        />
                    </div>
                </div>
            </>
        );
    }

    private initializeFilterOptionsDropdown() {
        if (this.filterOptionsItems.length > 0) {
            return;
        }

        this.filterOptionsItems.push({
            id: LinkQueryMode.LinksRecursive.toString(),
            text: Resources.MatchTopLevelWorkItemsFirst
        });
        this.filterOptionsItems.push({
            id: LinkQueryMode.LinksRecursiveReturnMatchingChildren.toString(),
            text: Resources.MatchLinkedWorkItemsFirst
        });

        this.filterOptionsSelection = new DropdownSelection();
        const selectedFilterOptionIndex = this.filterOptionsItems.findIndex(item => item.id === this.props.editInfo.mode.toString());
        if (selectedFilterOptionIndex > -1) {
            this.filterOptionsSelection.select(selectedFilterOptionIndex);
        } else {
            // select the 1st option by default
            this.filterOptionsSelection.select(0);
            this.props.setTreeState(+this.filterOptionsItems[0].id);
        }
    }

    private initializeTreeTypeDropdown() {
        if (this.treeTypeItems.length > 0) {
            return;
        }

        this.props.workItemLinkTypes
            .filter(linkType => linkType.topology === "Tree")
            .forEach(link => {
                this.treeTypeItems.push({ id: link.forwardEnd.name, text: link.reverseEnd.name + "/" + link.forwardEnd.name });
            });

        this.treeTypeSelection = new DropdownSelection();
        const selectedTreeTypeIndex = this.treeTypeItems.findIndex(item => item.id === this.props.editInfo.treeLinkTypes);
        if (selectedTreeTypeIndex > -1) {
            this.treeTypeSelection.select(selectedTreeTypeIndex);
        } else {
            // select the 1st option by default
            this.treeTypeSelection.select(0);
            this.props.setTreeState(undefined, undefined, this.treeTypeItems[0].id);
        }
    }
}
